import { useAuth0 } from '@auth0/auth0-react'
import { captureException } from '@sentry/react'
import axios from 'axios'
import { Suspense, useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import OrnaLogo from 'src/assets/ORNA Square Logo, transparent bg.png'
import CommonBanner from 'src/components/common/banner'
import Button from 'src/components/common/button'
import openNotification from 'src/components/common/notification'
import Spinner from 'src/components/common/spinner'
import alerts from 'src/constants/alerts'
import { APP_ENV } from 'src/env'
import routes from 'src/routes'
import { handlePromise } from 'src/utils/handlePromise'
import useLogout from 'src/utils/hooks/useLogout'
import useQueryStrings from 'src/utils/hooks/useQueryStrings'
import logger from 'src/utils/logger'

import ErrorBoundary from './errorboundary'
import './styles.css'

const AcceptInviteContainer = () => {
    const history = useHistory()

    const searchParams = useQueryStrings()
    const inviteToken = searchParams.get('token')

    const [password, setPassword] = useState('')
    const [confirmedPwd, setConfirmedPwd] = useState('')
    const [httpError, setHttpError] = useState(null)
    const [tokenChecking, setTokenChecking] = useState(false)
    const [tokenCheckError, setTokenCheckError] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [accountActive, setAccountActive] = useState(false)
    const [passwordError, setPasswordError] = useState(null)
    const [passwordValid, setPasswordValid] = useState(false)

    const { isLoading, isAuthenticated } = useAuth0()
    const logout = useLogout()

    // runs on mount
    useEffect(() => {
        setTokenChecking(true)

        const searchParams = new URLSearchParams({
            checkToken: true,
            token: inviteToken,
        })

        axios
            .get(
                `${APP_ENV.REACT_APP_ORNA_BACKEND_URL}/team/accept-invite-link?${searchParams.toString()}`
            )
            .then(({ data }) => {
                setTokenChecking(false)
                setAccountActive(data.accountActive)
            })
            .catch((error) => {
                setTokenChecking(false)
                setTokenCheckError(error.response.data?.error)

                if (error?.response?.data?.error === 'Invalid Link') {
                    openNotification('error', 'Invalid Invitation Link', 3)
                    history.replace('/')
                }
            })
    }, [history, inviteToken])

    const handlePasswordChange = (event) => {
        const value = event.target.value
        let errors = {}
        if (!value || value.length < 11) errors.validLength = false
        if (!/[A-Z]/.test(value)) errors.containsUppercase = false
        if (!/[a-z]/.test(value)) errors.containsLowercase = false
        if (!/\d/.test(value)) errors.containsNumber = false
        if (!/\W/.test(value)) errors.containsSpecial = false
        if (Object.keys(errors).length > 0) {
            setPasswordError({ ...errors })
            setPasswordValid(false)
        } else {
            setPasswordError(null)
            setPasswordValid(true)
        }

        setPassword(value)
    }

    const onSetPassword = useCallback(async () => {
        localStorage.removeItem('access_token')
        localStorage.removeItem('userOrganizationId')
        localStorage.removeItem('userOrganizations')

        openNotification('success', 'Setting your password..', 3)

        const searchParams = new URLSearchParams({
            token: inviteToken,
            password: password,
        })

        setSubmitting(true)
        /**
         * @type [import('axios').AxiosError, import('axios').AxiosResponse]
         */
        const [requestError, requestResponse] = await handlePromise(
            axios.get(
                `${APP_ENV.REACT_APP_ORNA_BACKEND_URL}/team/accept-invite-link?${searchParams.toString()}`
            )
        )
        setSubmitting(false)

        if (requestError) {
            if (
                requestError.response?.data?.passwordErrors &&
                Object.keys(requestError.response?.data?.passwordErrors)
                    .length > 0
            ) {
                setPasswordError({
                    ...requestError.response.data.passwordErrors,
                })
                openNotification(
                    alerts.ERROR,
                    'Password doest not fulfill all requirements',
                    2
                )
            } else {
                logger.error(requestError.response.data.error)
                captureException(requestError, {
                    extra: {
                        message: {
                            content: requestError.response.data.error,
                        },
                    },
                })
                openNotification('error', `An unexpected error occurred`)
            }
        } else {
            const isOk = requestResponse.data.message === 'ok'

            if (isOk) {
                openNotification('success', 'Password Set Successfully', 3)
                setHttpError(false)
                setConfirmedPwd('')
                setPassword('')
                history.push(routes.login)
            }
        }
    }, [history, inviteToken, password])

    const renderErrorMsg = (error) => {
        switch (error) {
            case 'Link not valid':
                return (
                    <>
                        <div>
                            Sorry, the link you have entered is either incorrect
                            or expired or already used.
                        </div>
                        <div>
                            Please contact your organization administrator if
                            you are facing any issue.
                        </div>
                    </>
                )
            case 'Link has expired':
            case 'Link expired':
                return (
                    <>
                        <div>Sorry, the link you have entered has expired.</div>
                        <div>
                            Please contact your organization administrator to
                            send a new invite.
                        </div>
                    </>
                )
            case 'Link Used':
                return (
                    <>
                        <div>Sorry, this invitation link has been used.</div>
                        <div>
                            Please contact your organization administrator if
                            you are facing any issue.
                        </div>
                    </>
                )
            default:
                return (
                    <>
                        <div>Sorry, something went wrong.</div>
                        <div>
                            Please contact your organization administrator if
                            you are facing any issue.
                        </div>
                    </>
                )
        }
    }

    const renderView = (accountActive) => {
        if (accountActive) {
            return (
                <center>
                    <div className="px-4 py-5 text-3xl text-white">
                        Accept Invite
                    </div>
                    <div className="px-4 py-4 text-base text-neutral-gray-75">
                        Click here to accept invite
                    </div>
                    <Button
                        intent={'primary'}
                        size={'large'}
                        disabled={submitting}
                        onClick={async () => {
                            localStorage.removeItem('access_token')
                            localStorage.removeItem('userOrganizationId')
                            localStorage.removeItem('userOrganizations')

                            const searchParams = new URLSearchParams({
                                token: inviteToken,
                                password: password,
                            })

                            openNotification(
                                'success',
                                'Accepting Invitation..'
                            )

                            setSubmitting(true)
                            /**
                             * @type [import('axios').AxiosError, import('axios').AxiosResponse]
                             */
                            const [requestError, requestResponse] =
                                await handlePromise(
                                    axios.get(
                                        `${APP_ENV.REACT_APP_ORNA_BACKEND_URL}/team/accept-invite-link?${searchParams.toString()}`
                                    )
                                )
                            setSubmitting(false)

                            if (requestError) {
                                logger.error(requestError.response.data.error)
                                captureException(requestError, {
                                    extra: {
                                        message: {
                                            content:
                                                requestError.response.data
                                                    .error,
                                        },
                                    },
                                })
                                openNotification(
                                    'error',
                                    requestError.response.data.error ??
                                        `An unexpected error occurred`
                                )
                            } else {
                                const isOk =
                                    requestResponse.data.message === 'ok'

                                if (isOk) {
                                    openNotification(
                                        'success',
                                        'Invitation Accepted',
                                        3
                                    )

                                    setHttpError(false)
                                    setConfirmedPwd('')
                                    setPassword('')
                                    if (isAuthenticated) {
                                        logout()
                                    } else {
                                        history.push(routes.login)
                                    }
                                }
                            }
                        }}
                    >
                        {submitting ? `ACCEPT INVITE` : `ACCEPT INVITE`}
                    </Button>
                </center>
            )
        } else {
            return (
                <>
                    <center className="required py-5 text-3xl text-white">
                        Set Password
                    </center>
                    <div className="mt-2 2xl:mt-4">
                        <input
                            className="w-full rounded-md bg-neutral-gray-600 px-2 py-2 text-sm text-neutral-gray-75 focus:outline-none focus:ring-1 focus:ring-primary-red-500 2xl:px-2 2xl:py-2 2xl:text-base"
                            placeholder="Enter Password"
                            type="password"
                            value={password}
                            required
                            name="password"
                            onChange={handlePasswordChange}
                        />
                    </div>
                    <div className="mt-2 2xl:mt-4">
                        <input
                            className="w-full rounded-md bg-neutral-gray-600 px-2 py-2 text-sm text-neutral-gray-75 focus:outline-none focus:ring-1 focus:ring-primary-red-500 2xl:px-2 2xl:py-2 2xl:text-base"
                            placeholder="Re-enter Password"
                            type="password"
                            required
                            value={confirmedPwd}
                            name="rePassword"
                            onChange={(e) => {
                                setConfirmedPwd(e.target.value)
                            }}
                        />
                    </div>
                    <div className="mt-2 rounded-md bg-neutral-gray-600 px-2 py-2 text-xs text-neutral-gray-300 2xl:mt-4">
                        <span className="font-bold">Password Rules:</span>
                        <ul>
                            <li>
                                -{' '}
                                <span
                                    className={`${
                                        passwordError?.validLength === false
                                            ? 'text-red-400'
                                            : ''
                                    }`}
                                >
                                    Minimum 11 characters
                                </span>
                            </li>
                            <li>
                                - No part of username can be part of password
                            </li>
                            <li>
                                -{' '}
                                <span
                                    className={`${
                                        passwordError?.containsUppercase ===
                                        false
                                            ? 'text-red-400'
                                            : ''
                                    }`}
                                >
                                    Upper case
                                </span>
                                ,{' '}
                                <span
                                    className={`${
                                        passwordError?.containsLowercase ===
                                        false
                                            ? 'text-red-400'
                                            : ''
                                    }`}
                                >
                                    lower case
                                </span>
                                ,{' '}
                                <span
                                    className={`${
                                        passwordError?.containsNumber === false
                                            ? 'text-red-400'
                                            : ''
                                    }`}
                                >
                                    at least 1 number
                                </span>{' '}
                                and at least{' '}
                                <span
                                    className={`${
                                        passwordError?.containsSpecial === false
                                            ? 'text-red-400'
                                            : ''
                                    }`}
                                >
                                    1 special character
                                </span>{' '}
                                are required
                            </li>
                        </ul>
                    </div>
                    {httpError && (
                        <div className="mt-1 2xl:mt-2">
                            <div className="py-2 text-sm text-primary-red-500">
                                {httpError}
                            </div>
                        </div>
                    )}

                    <div className="mt-8 flex w-full items-center justify-center px-6">
                        <Button
                            intent={'primary'}
                            size={'large'}
                            disabled={
                                !(
                                    password === confirmedPwd &&
                                    password !== '' &&
                                    !submitting &&
                                    passwordValid
                                )
                            }
                            onClick={onSetPassword}
                        >
                            {submitting ? `SETTING PASSWORD` : `SET PASSWORD`}
                        </Button>
                    </div>
                </>
            )
        }
    }

    return (
        <div className="h-full w-full">
            {tokenChecking || isLoading ? (
                <Spinner />
            ) : (
                <>
                    <Suspense
                        fallback={<div className="px-4 py-6">Loading...</div>}
                    >
                        <ErrorBoundary>
                            <div
                                id="accept-invite-container"
                                className="flex h-full w-full"
                            >
                                <div className="banner-container flex h-full w-8/12 flex-col justify-between py-10 pl-14">
                                    <CommonBanner />
                                </div>

                                <div className="h-full w-4/12 bg-neutral-gray-500">
                                    <div className="flex h-full w-full items-center justify-center">
                                        <div>
                                            <div className="mt-8">
                                                <img
                                                    className="mx-auto h-24 w-24"
                                                    src={OrnaLogo}
                                                    alt="orna logo"
                                                />
                                            </div>
                                            {tokenCheckError ? (
                                                <>
                                                    <center className="required px-10 py-5 text-base text-white">
                                                        {renderErrorMsg(
                                                            tokenCheckError
                                                        )}
                                                        <Button
                                                            extendClass="mt-4"
                                                            size="large"
                                                            label="Back to Login"
                                                            intent="primary"
                                                            onClick={() => {
                                                                history.push(
                                                                    routes.login
                                                                )
                                                            }}
                                                        />
                                                    </center>
                                                </>
                                            ) : (
                                                <>{renderView(accountActive)}</>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ErrorBoundary>
                    </Suspense>
                </>
            )}
        </div>
    )
}

export default AcceptInviteContainer
